/**
 * TutorialContext — orquestación del tutorial de onboarding (KAN-232).
 *
 * Puente entre los módulos declarativos (src/content/tutorial) y el sistema
 * de guías AR existente (ARGuideContext + ARGuideOverlay): al iniciar un
 * módulo se mapean sus pasos a GuideSteps (highlight sobre anclas
 * data-ar-guide) y este provider se encarga de:
 *
 *   - navegar a la ruta de cada paso (multi-página),
 *   - persistir el progreso por paso (localStorage, por usuario),
 *   - auto-completar módulos cuyos datos ya existen (merge al arrancar),
 *   - al terminar: marcar completado/visto y volver al hub (/tutorial)
 *     sugiriendo el siguiente módulo.
 *
 * El provider renderiza sus side-effects internamente (no hay que montar
 * ningún runner aparte) y no agrega UI propia: la visual es la del overlay
 * de guías AR.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useARGuide } from '../../contexts/ARGuideContext';
import type { ARGuide, GuideStep } from '../../types/arGuides';
import { TUTORIAL_MODULES } from '../../content/tutorial';
import type { TutorialModuleDef } from '../../services/tutorial/types';
import {
  getTutorialState,
  markStepDone,
  markModuleCompleted,
  markModuleSeen,
  mergeAutoCompletion,
} from '../../services/tutorial/tutorialProgress';
import { getCurrentUserInfo } from '../../services/storage';
import {
  leerPlaybackRate,
  guardarPlaybackRate,
  isPlaybackRate,
  type PlaybackRate,
} from '../../services/tutorial/playbackRate';
import {
  iniciarCorrida,
  asegurarCorrida,
  registrarCreate,
  suscribirTracker,
  obtenerCorrida,
} from '../../services/tutorial/practicaTracker';
import { ModalLimpiezaPractica } from './ModalLimpiezaPractica';

const PREFIX = 'tutorial:';

/** URL pública del audio de un paso (exista o no — GuideControls lo oculta si falla). */
export const tutorialAudioUrl = (moduleId: string, stepId: string): string =>
  `${process.env.PUBLIC_URL || ''}/audio/tutorial/${moduleId}/${stepId}.mp3`;

interface TutorialContextValue {
  /** Inicia (o retoma) un módulo. `replay` fuerza desde el paso 0. */
  startModule: (moduleId: string, opts?: { replay?: boolean }) => void;
  /** Corta el módulo activo (mismo efecto que la × del panel). */
  cancelModule: () => void;
  /** Módulo con guía activa, o null. */
  activeModuleId: string | null;
  /** Abre el modal de limpieza de práctica para una corrida pendiente. */
  abrirLimpieza: (moduleId: string) => void;
  /** Velocidad de la narración (1 | 1.25 | 1.5 | 2). Persistida por usuario. */
  playbackRate: PlaybackRate;
  /** Cambia la velocidad de la narración y la persiste. */
  setPlaybackRate: (rate: PlaybackRate) => void;
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

export const useTutorial = (): TutorialContextValue => {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used within TutorialProvider');
  return ctx;
};

/**
 * Variante sin throw para componentes compartidos con las guías AR genéricas
 * (GuideControls también sirve guías del chat y puede montarse fuera del
 * TutorialProvider, ej. en tests): undefined si no hay provider arriba.
 */
export const useTutorialOpcional = (): TutorialContextValue | undefined =>
  useContext(TutorialContext);

const moduleById = (id: string): TutorialModuleDef | undefined =>
  TUTORIAL_MODULES.find(m => m.id === id);

/** Mapea los pasos del módulo a pasos de guía AR (highlight + label). */
const buildGuideSteps = (module: TutorialModuleDef): GuideStep[] =>
  module.steps.map((step, i) => ({
    stepId: step.id,
    order: i,
    text: step.text,
    expectedRoute: step.route ? step.route.split('?')[0] : undefined,
    audioUrl: step.narration ? tutorialAudioUrl(module.id, step.id) : undefined,
    // Default: pasos con ancla avanzan al clickear el elemento; los campos
    // de formulario lo desactivan (llenar no es "hacer clic y listo").
    advanceOnClick: step.advanceOnClick ?? Boolean(step.target),
    advanceWhenVisible: step.advanceWhenVisible,
    elements: step.target
      ? ([
          { type: 'highlight', targetSelector: step.target, shape: 'rectangle', animation: 'pulse' },
          // Anclas extra: mismo highlight visual, pero no disparan el avance.
          ...(step.extraTargets ?? []).map(t => ({
            type: 'highlight',
            targetSelector: t,
            shape: 'rectangle',
            animation: 'pulse',
            visualOnly: true,
          })),
          // Anclas alternativas de avance: mismo highlight Y su clic también
          // avanza (ej: el mismo formulario que se abre desde dos botones).
          ...(step.advanceTargets ?? []).map(t => ({
            type: 'highlight',
            targetSelector: t,
            shape: 'rectangle',
            animation: 'pulse',
          })),
          ...(step.label
            ? [{ type: 'label', text: step.label, position: { near: step.target }, style: 'bubble' }]
            : []),
        ] as GuideStep['elements'])
      : [],
  }));

export const TutorialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeGuide, currentStepIndex, startGuide, cancelGuide, nextStep } = useARGuide();

  const [userId, setUserId] = useState('');
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  // Velocidad de la narración: al arrancar se lee la clave global (aún no hay
  // usuario); cuando el userId llega, re-leemos su clave (que cae a la global
  // si no existe aún). La aplica GuideControls al <audio> de cada paso.
  const [playbackRate, setPlaybackRateState] = useState<PlaybackRate>(() => leerPlaybackRate());
  // Modal de limpieza de práctica: se abre al terminar/salir de un módulo
  // cuya corrida tiene ítems sin decidir (bloqueante: decide o decide).
  const [limpiezaAbierta, setLimpiezaAbierta] = useState<string | null>(null);

  // ---- userId + auto-completado por datos existentes (una vez por sesión) ----
  const mergedRef = useRef(false);
  useEffect(() => {
    let reintentos = 0;
    let cancelado = false;
    const obtener = async () => {
      try {
        const info = await getCurrentUserInfo();
        if (!info.userId) throw new Error('sin usuario');
        if (cancelado) return;
        setUserId(info.userId);
        // Velocidad de narración: la clave por usuario gana sobre la global.
        setPlaybackRateState(leerPlaybackRate(info.userId));
        if (!mergedRef.current) {
          mergedRef.current = true;
          // Usuarios con campo/ubicaciones/animales/puesto ya cargados no son
          // molestados: esos módulos nacen completados.
          await mergeAutoCompletion(info.userId, TUTORIAL_MODULES);
        }
      } catch {
        if (!cancelado && reintentos < 5) {
          reintentos += 1;
          setTimeout(() => void obtener(), 1000 * reintentos);
        }
      }
    };
    void obtener();
    return () => {
      cancelado = true;
    };
  }, []);

  // ---- API ----
  const startModule = useCallback(
    (moduleId: string, opts?: { replay?: boolean }) => {
      const module = moduleById(moduleId);
      if (!module) return;
      const uid = userId;
      const state = uid ? getTutorialState(uid) : null;
      const status = state?.modules[moduleId]?.status ?? 'not_started';
      const replay = opts?.replay || status === 'completed';

      // Retomar: primer paso no hecho. Replay: desde el principio.
      const stepsDone = state?.modules[moduleId]?.stepsDone ?? [];
      const startIndex = replay
        ? 0
        : Math.max(0, module.steps.findIndex(s => !stepsDone.includes(s.id)));

      const guide: ARGuide = {
        guideId: `${PREFIX}${moduleId}`,
        taskId: `tutorial-${moduleId}`,
        currentStep: startIndex,
        status: 'active',
        steps: buildGuideSteps(module),
      };
      startGuide(guide);
      setActiveModuleId(moduleId);

      // Tracker de práctica (limpieza al cierre): empezar desde cero abre una
      // corrida limpia; retomar conserva lo ya registrado. Sin userId todavía
      // (deep-link autoplay), lo asegura el effect de suscripción cuando llega.
      if (uid) {
        if (startIndex === 0) iniciarCorrida(uid, moduleId);
        else asegurarCorrida(uid, moduleId);
      }
    },
    [startGuide, userId],
  );

  const cancelModule = useCallback(() => {
    cancelGuide();
    setActiveModuleId(null);
  }, [cancelGuide]);

  /** Abre el modal de limpieza para una corrida con ítems sin decidir. */
  const abrirLimpieza = useCallback((moduleId: string) => {
    setLimpiezaAbierta(moduleId);
  }, []);

  /** Cambia la velocidad de la narración y la persiste (por usuario si hay). */
  const setPlaybackRate = useCallback(
    (rate: PlaybackRate) => {
      if (!isPlaybackRate(rate)) return; // defensivo: sólo velocidades del selector
      setPlaybackRateState(rate);
      guardarPlaybackRate(rate, userId || undefined);
    },
    [userId],
  );

  // ---- Tracker de práctica: mientras un módulo está activo, escuchar los
  // CREATEs de repositorio (animales del emitter local, gastos/ingresos/
  // lluvias/recordatorios del de sync) y anotarlos en la corrida. El panel de
  // limpieza del hub los ofrece al cierre (ver practicaTracker).
  useEffect(() => {
    if (!activeModuleId || !userId) return;
    asegurarCorrida(userId, activeModuleId);
    const off = suscribirTracker(item => registrarCreate(userId, activeModuleId, item));
    return off;
  }, [activeModuleId, userId]);

  // ---- Runner: reacciona al estado de la guía AR activa ----
  const isTutorialGuide = Boolean(activeGuide?.guideId.startsWith(PREFIX));
  const guideModuleId = isTutorialGuide ? activeGuide!.guideId.slice(PREFIX.length) : null;
  const guideModule = guideModuleId ? moduleById(guideModuleId) : null;

  // Navegación multi-página: al entrar a un paso cuya ruta no es la actual.
  // Compara pathname + search: pasos como el del puesto dependen del query
  // param (accion=poner-puesto) aunque el pathname no cambie.
  useEffect(() => {
    if (!isTutorialGuide || !guideModule) return;
    const step = guideModule.steps[currentStepIndex];
    if (!step?.route) return;
    if (location.pathname + location.search !== step.route) {
      navigate(step.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialGuide, guideModule?.id, currentStepIndex]);

  // Pasos condicionales (showIf): evaluados AL ENTRAR al paso — no al armar la
  // guía — porque la condición puede cambiar a mitad de módulo (ej: pesajes
  // cierra la sesión 1 y abre la 2). Si no cumple, el paso se saltea: se marca
  // done (para que el resume no re-aterrice en él y el conteo del módulo
  // cierre) y se avanza. Condicionales consecutivos se saltean en cadena: cada
  // incremento de índice re-dispara este efecto.
  useEffect(() => {
    if (!isTutorialGuide || !guideModule) return;
    const step = guideModule.steps[currentStepIndex];
    if (step?.showIf && !step.showIf()) {
      if (userId) markStepDone(userId, guideModule.id, step.id, guideModule.steps.length);
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialGuide, guideModule?.id, currentStepIndex, userId]);

  // Progreso: al avanzar de índice, el paso anterior queda hecho.
  const prevIndexRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isTutorialGuide || !guideModule || !userId) {
      prevIndexRef.current = null;
      return;
    }
    const prev = prevIndexRef.current;
    if (prev !== null && currentStepIndex > prev) {
      const paso = guideModule.steps[prev];
      if (paso) markStepDone(userId, guideModule.id, paso.id, guideModule.steps.length);
    }
    prevIndexRef.current = currentStepIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialGuide, guideModule?.id, currentStepIndex, userId]);

  // Fin de guía: módulo completado + visto, y vuelta al hub con sugerencia.
  // Tanto al COMPLETAR como al SALIR con la ✕: si la corrida tiene ítems sin
  // decidir, se abre el modal bloqueante de limpieza de práctica.
  useEffect(() => {
    if (!isTutorialGuide || !activeGuide || !userId) return;
    if (activeGuide.status === 'completed') {
      const id = activeGuide.guideId.slice(PREFIX.length);
      markModuleCompleted(userId, id);
      markModuleSeen(userId, id);
      setActiveModuleId(null);
      navigate(`/tutorial?completado=${id}`);
      const corrida = obtenerCorrida(userId, id);
      if (corrida && corrida.items.length > 0 && !corrida.resuelta) {
        setLimpiezaAbierta(id);
      }
    } else if (activeGuide.status === 'cancelled') {
      const id = activeGuide.guideId.slice(PREFIX.length);
      setActiveModuleId(null);
      const corrida = userId ? obtenerCorrida(userId, id) : null;
      if (corrida && corrida.items.length > 0 && !corrida.resuelta) {
        setLimpiezaAbierta(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGuide?.status, isTutorialGuide, userId]);

  const moduloLimpieza = limpiezaAbierta
    ? TUTORIAL_MODULES.find(m => m.id === limpiezaAbierta)
    : null;

  return (
    <TutorialContext.Provider
      value={{ startModule, cancelModule, activeModuleId, abrirLimpieza, playbackRate, setPlaybackRate }}
    >
      {children}
      {limpiezaAbierta && userId && moduloLimpieza && (
        <ModalLimpiezaPractica
          userId={userId}
          moduleId={limpiezaAbierta}
          moduleTitle={moduloLimpieza.title}
          onClose={() => setLimpiezaAbierta(null)}
        />
      )}
    </TutorialContext.Provider>
  );
};
